import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import GetAppIcon from "@material-ui/icons/GetApp"
import HistoryIcon from "@material-ui/icons/History"
import RefreshIcon from "@material-ui/icons/Refresh"
import { useCallback, useEffect, useState } from "react"
import { alertStart } from "../../../routes"
import { listarHistoricoCardapio } from "../../../services/cardapio"
import { getIdEstabelecimentoAtual } from "../../../services/quitacaoAutomatica"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { useCardapioContext } from "./CardapioContext"

const useStyles = makeStyles(theme => ({
    container: {
        width: "100%",
        maxWidth: 900,
        margin: "auto",
        padding: theme.spacing(2),
        textAlign: "left",
        [theme.breakpoints.up("md")]: {
            paddingTop: "6rem",
        },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    headerTitulo: {
        display: "flex",
        alignItems: "center",
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    legenda: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(2),
    },
    vazio: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(4),
    },
    botaoBaixar: {
        borderRadius: 20,
        textTransform: "none",
        fontWeight: 600,
        color: "#4caf50",
        borderColor: "#4caf50",
    },
}))

// Nome dos meses so pra montar um rotulo amigavel sem depender de locale.
const MESES = [
    "jan",
    "fev",
    "mar",
    "abr",
    "mai",
    "jun",
    "jul",
    "ago",
    "set",
    "out",
    "nov",
    "dez",
]

// O backend grava cada backup com o nome:
//   {id_zero_pad_8}_{dd-MM-yyyy HH:mm:ss}_{responsavel}.csv
// dentro de cardapio/{idEstabelecimento}/. A URL vem completa (com espacos crus).
// Aqui extraimos data e responsavel a partir desse padrao de nome.
function parseItem(url) {
    const item = { url, nomeArquivo: url, data: null, dataLabel: "-", responsavel: "-" }
    try {
        const semQuery = url.split("?")[0]
        const nomeArquivo = decodeURIComponent(semQuery.substring(semQuery.lastIndexOf("/") + 1))
        item.nomeArquivo = nomeArquivo

        const semExt = nomeArquivo.replace(/\.csv$/i, "")
        const partes = semExt.split("_")
        if (partes.length >= 3) {
            const dataStr = partes[1]
            item.responsavel = partes.slice(2).join("_") || "-"

            // dataStr = "dd-MM-yyyy HH:mm:ss"
            const m = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})$/.exec(dataStr)
            if (m) {
                const [, dd, mm, yyyy, hh, mi, ss] = m
                item.data = new Date(
                    Number(yyyy),
                    Number(mm) - 1,
                    Number(dd),
                    Number(hh),
                    Number(mi),
                    Number(ss)
                )
                item.dataLabel = `${dd}/${mm}/${yyyy} ${hh}:${mi}`
            } else {
                item.dataLabel = dataStr
            }
        }
    } catch (e) {
        // Se o nome fugir do padrao, mantemos os defaults e ainda permitimos baixar a URL.
    }
    return item
}

export default function HistoricoCardapio() {
    const classes = useStyles()
    const { estabelecimento } = useCardapioContext()
    const setLoading = useLoadingStore(s => s.setLoading)
    const perfil = useAppStore(s => s.configuracoes?.user?.perfil)
    const ehClienteFiel = perfil === "CLIENTEFIEL"
    const autorizacao = localStorage.getItem("chaveSistema")

    const [itens, setItens] = useState([])
    const [carregou, setCarregou] = useState(false)

    const carregar = useCallback(async () => {
        // Usa a LOJA SELECIONADA no seletor (estabelecimentoAtualCF), nao a loja do
        // usuario logado — o contexto do cardapio pode apontar para a loja do usuario.
        const idEstabelecimento = getIdEstabelecimentoAtual() || estabelecimento?.id
        if (!autorizacao || !idEstabelecimento) return
        try {
            setLoading(true)
            const resposta = await listarHistoricoCardapio(idEstabelecimento, autorizacao)
            if (resposta && "retornoErro" in resposta) {
                alertStart(resposta.mensagem || "Erro ao carregar o histórico", "error")
                setItens([])
                return
            }
            const lista = Array.isArray(resposta) ? resposta : []
            const parseados = lista
                .map(v => parseItem(v?.valor ?? v))
                .filter(i => i.url)
                .sort((a, b) => {
                    const ta = a.data ? a.data.getTime() : 0
                    const tb = b.data ? b.data.getTime() : 0
                    return tb - ta
                })
            setItens(parseados)
        } catch (error) {
            alertStart("Erro ao carregar o histórico", "error")
            setItens([])
        } finally {
            setCarregou(true)
            setLoading(false)
        }
    }, [autorizacao, estabelecimento, setLoading])

    useEffect(() => {
        carregar()
    }, [carregar])

    // A URL do S3 vem com espacos crus no nome; encodeURI deixa o link valido.
    // Tentamos baixar como blob (nome de arquivo correto); se o CORS do bucket
    // bloquear, caimos para abrir a URL direto numa nova aba.
    const baixar = async item => {
        const href = encodeURI(item.url)
        try {
            setLoading(true)
            const resp = await fetch(href)
            if (!resp.ok) throw new Error("HTTP " + resp.status)
            const blob = await resp.blob()
            const objectUrl = URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = objectUrl
            link.download = item.nomeArquivo || "cardapio.csv"
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(objectUrl)
        } catch (e) {
            // Fallback: abre direto (o navegador baixa o CSV ou exibe).
            window.open(href, "_blank", "noopener")
        } finally {
            setLoading(false)
        }
    }

    const dataLabelCompleta = item =>
        item.data
            ? `${String(item.data.getDate()).padStart(2, "0")} de ${
                  MESES[item.data.getMonth()]
              } de ${item.data.getFullYear()} às ${item.dataLabel.split(" ")[1] || ""}`
            : item.dataLabel

    return (
        <Box className={classes.container}>
            <Paper className={classes.configCard}>
                <Box className={classes.cardHeader}>
                    <Box className={classes.headerTitulo}>
                        <HistoryIcon className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Histórico de cardápio
                        </Typography>
                    </Box>
                    <Tooltip title="Atualizar">
                        <IconButton onClick={carregar} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box className={classes.cardContent}>
                    <Typography variant="body2" className={classes.legenda}>
                        A cada alteração no cardápio é guardada uma cópia (CSV). Baixe qualquer
                        versão anterior para consultar ou restaurar manualmente.
                    </Typography>

                    {itens.length === 0 ? (
                        <Typography className={classes.vazio}>
                            {carregou
                                ? "Nenhuma versão salva foi encontrada para este estabelecimento."
                                : "Carregando…"}
                        </Typography>
                    ) : (
                        <TableContainer style={{ maxHeight: 560 }}>
                            <Table stickyHeader size="small" aria-label="Histórico de cardápio">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Data</TableCell>
                                        <TableCell>Responsável</TableCell>
                                        <TableCell align="right">Arquivo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {itens.map((item, i) => (
                                        <TableRow hover key={`${item.nomeArquivo}-${i}`}>
                                            <TableCell>
                                                <Tooltip title={dataLabelCompleta(item)}>
                                                    <span>{item.dataLabel}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {ehClienteFiel ? (
                                                    item.responsavel
                                                ) : (
                                                    <Tooltip title={item.responsavel}>
                                                        <span>{item.responsavel}</span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    className={classes.botaoBaixar}
                                                    startIcon={<GetAppIcon />}
                                                    onClick={() => baixar(item)}
                                                >
                                                    Baixar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Paper>
        </Box>
    )
}
